import React, { useEffect, useState, useRef } from "react";
import { useAtom } from "jotai";
import { Tooltip } from "@mui/material";
import axios from "axios";
import toast from "react-hot-toast";
import { confirmAlert } from "react-confirm-alert";
import { gbState } from "../../../components/shared/GlobalState";
import {
  HiUserGroup,
  HiSearch,
  HiX,
  HiSortAscending,
  HiSortDescending,
  HiEye,
  HiShieldCheck,
  HiUserCircle,
  HiMail,
  HiPhone,
  HiCalendar,
  HiLocationMarker,
} from "react-icons/hi";
import CardBackground from "../../../components/MainTemplate/CardBackground";
import ViewSubscriberModal from "./ViewSubscriberModal";

const ChurchSubscribersChild = () => {
  const [{ errorLogApi, placeHolderText, Category, user }] = useAtom(gbState);
  const [data, setData] = useState(null);
  const [filteredData, setFilteredData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const itemsPerPage = 10;

  const fetchSubscribers = async () => {
    let userToken = localStorage.getItem("token");
    let urlLink = `${process.env.REACT_APP_API_URL}/user/subscribers/${user.church_id}`;
    const config = {
      method: "GET",
      url: urlLink,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };
    axios(config)
      .then(function (response) {
        const oldData = response.data.members;
        setData(oldData);
        setFilteredData(oldData);
      })
      .catch(function (err) {
        console.log("error api call ", err);
        toast.error("Failed to fetch subscribers");
      });
  };

  useEffect(() => {
    fetchSubscribers();
  }, []);

  // Handle search input change
  const handleSearchChange = (e) => {
    const searchValue = e.target.value.toLowerCase();
    setSearchTerm(searchValue);
    setCurrentPage(1); // Reset to first page on search

    if (!searchValue.trim()) {
      setFilteredData(data);
      return;
    }

    // Filter by name, email, or phone
    const filtered = data?.filter((subscriber) => {
      const firstName = subscriber.first_name?.toLowerCase() || "";
      const lastName = subscriber.last_name?.toLowerCase() || "";
      const email = subscriber.email?.toLowerCase() || "";
      const phone = subscriber.mobile?.toLowerCase() || "";

      return (
        firstName.includes(searchValue) ||
        lastName.includes(searchValue) ||
        email.includes(searchValue) ||
        phone.includes(searchValue)
      );
    });

    setFilteredData(filtered);
  };

  // Clear search input
  const clearSearch = () => {
    setSearchTerm("");
    setFilteredData(data);
    setCurrentPage(1);
  };

  // Handle sorting
  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });

    const sortedData = [...filteredData].sort((a, b) => {
      if (a[key] === null) return 1;
      if (b[key] === null) return -1;
      if (a[key] === undefined) return 1;
      if (b[key] === undefined) return -1;

      if (a[key] < b[key]) {
        return direction === "ascending" ? -1 : 1;
      }
      if (a[key] > b[key]) {
        return direction === "ascending" ? 1 : -1;
      }
      return 0;
    });

    setFilteredData(sortedData);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <HiSortAscending className="w-4 h-4 text-gray-400" />;
    }
    return sortConfig.direction === "ascending" ? (
      <HiSortAscending className="w-4 h-4 text-indigo-500" />
    ) : (
      <HiSortDescending className="w-4 h-4 text-indigo-500" />
    );
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData?.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil((filteredData?.length || 0) / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Action handlers
  const handleView = async (subscriber) => {
    try {
      let userToken = localStorage.getItem("token");
      // Fetch family details if user is a sub-member
      if (subscriber.user_type === "Sub Member") {
        const familyConfig = {
          method: "GET",
          url: `${process.env.REACT_APP_API_URL}/family/sub-member/${subscriber.church_id._id}`,
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
        };
        try {
          const familyResponse = await axios(familyConfig);
          console.log("Family Response:", familyResponse.data); // Debug log
          if (familyResponse.data && familyResponse.data.families) {
            subscriber.familyDetails = familyResponse.data.families;
          } else {
            console.log("No family details found in response"); // Debug log
            subscriber.familyDetails = null;
          }
        } catch (familyError) {
          console.error("Error fetching family details:", familyError); // Debug log
          toast.error("Failed to fetch family details. Please try again.");
          errorLogApi(
            familyError,
            "ChurchSubscribersChild",
            "/family/sub-member"
          );
          // Continue with user details even if family fetch fails
          subscriber.familyDetails = null;
        }
      }
      setSelectedUser(subscriber);
      setIsModalOpen(true);
    } catch (error) {
      console.error("Error in handleView:", error); // Debug log
      toast.error("Failed to load user details");
      errorLogApi(error, "ChurchSubscribersChild", "handleView");
    }
  };

  const handleMakeTrustee = (subscriber) => {
    confirmAlert({
      title: "Confirm Action",
      message: "Are you sure you want to make this user a trustee?",
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            let userToken = localStorage.getItem("token");
            const config = {
              method: "post",
              url: `${process.env.REACT_APP_API_URL}/user/make-trustee/`,
              headers: {
                Authorization: `Bearer ${userToken}`,
              },
              data: {
                userId: subscriber._id,
                churchId: user.church_id,
              },
            };

            axios(config)
              .then(function (response) {
                if (response) {
                  toast.success("User has been made a trustee successfully");
                  // Refresh the data
                  fetchSubscribers();
                } else {
                  toast.error(
                    response.data.message || "Failed to make user a trustee"
                  );
                }
              })
              .catch(function (error) {
                toast.error(
                  error?.response?.data?.message ||
                    "Failed to make user a trustee"
                );
                errorLogApi(
                  error,
                  "ChurchSubscribersChild",
                  "/user/make-trustee"
                );
              });
          },
        },
        {
          label: "No",
        },
      ],
    });
  };

  const handleRemoveTrustee = (subscriber) => {
    confirmAlert({
      title: "Confirm Action",
      message: "Are you sure you want to remove this user as a trustee?",
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            let userToken = localStorage.getItem("token");
            const config = {
              method: "post",
              url: `${process.env.REACT_APP_API_URL}/user/remove-trustee/`,
              headers: {
                Authorization: `Bearer ${userToken}`,
              },
              data: {
                userId: subscriber._id,
              },
            };
            axios(config)
              .then(function (response) {
                if (response) {
                  toast.success(
                    "User has been removed as a trustee successfully"
                  );
                  fetchSubscribers();
                } else {
                  toast.error(
                    response.data.message ||
                      "Failed to remove user as a trustee"
                  );
                }
              })
              .catch(function (error) {
                toast.error(
                  error?.response?.data?.message ||
                    "Failed to remove user as a trustee"
                );
                errorLogApi(
                  error,
                  "ChurchSubscribersChild",
                  "/user/remove-trustee"
                );
              });
          },
        },
        {
          label: "No",
        },
      ],
    });
  };

  const handleDelegateAdmin = (subscriber) => {
    confirmAlert({
      title: "Confirm Action",
      message: "Are you sure you want to delegate this user as Church Admin?",
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            let userToken = localStorage.getItem("token");
            if (!subscriber.email) {
              toast.error(
                `${subscriber.first_name} ${subscriber.last_name} has no email address, please add an email address to the user`
              );
              return;
            }
            const config = {
              method: "post",
              url: `${process.env.REACT_APP_API_URL}/user/update-secondary-role`,
              headers: {
                Authorization: `Bearer ${userToken}`,
              },
              data: {
                userId: subscriber._id,
                secondaryRole: "CHURCH_ADMIN",
              },
            };
            axios(config)
              .then(function (response) {
                if (response) {
                  toast.success(
                    "User has been delegated as admin successfully"
                  );
                  fetchSubscribers();
                } else {
                  toast.error(
                    response.data.message || "Failed to delegate admin rights"
                  );
                }
              })
              .catch(function (error) {
                toast.error(
                  error?.response?.data?.message ||
                    "Failed to delegate admin rights"
                );
                errorLogApi(
                  error,
                  "ChurchSubscribersChild",
                  "/user/update-secondary-role"
                );
              });
          },
        },
        {
          label: "No",
        },
      ],
    });
  };

  const handleRemoveAdmin = (subscriber) => {
    confirmAlert({
      title: "Confirm Action",
      message: "Are you sure you want to Remove this Admin Rights?",
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            let userToken = localStorage.getItem("token");
            const config = {
              method: "post",
              url: `${process.env.REACT_APP_API_URL}/user/remove-secondary-role`,
              headers: {
                Authorization: `Bearer ${userToken}`,
              },
              data: { userId: subscriber._id },
            };
            axios(config)
              .then(function (response) {
                if (response) {
                  toast.success("Admin rights have been removed successfully");
                  fetchSubscribers();
                } else {
                  toast.error(
                    response.data.message || "Failed to remove admin rights"
                  );
                }
              })
              .catch(function (error) {
                toast.error(
                  error?.response?.data?.message ||
                    "Failed to remove admin rights"
                );
                errorLogApi(
                  error,
                  "ChurchSubscribersChild",
                  "/user/remove-secondary-role"
                );
              });
          },
        },
        {
          label: "No",
        },
      ],
    });
  };

  // Get badge color based on user type
  const getUserTypeBadgeColor = (userType) => {
    switch (userType) {
      case "Member":
        return "bg-green-100 text-green-800";
      case "Sub Member":
        return "bg-blue-100 text-blue-800";
      case "Admin":
        return "bg-purple-100 text-purple-800";
      case "Trustee":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleDisableUser = (subscriber) => {
    confirmAlert({
      title: "Confirm Action",
      message: "Are you sure you want to disable this user?",
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            {
              let userToken = localStorage.getItem("token");
              const config = {
                method: "post",
                url: `${process.env.REACT_APP_API_URL}/user/set-account-status/`,
                headers: {
                  Authorization: `Bearer ${userToken}`,
                },
                data: {
                  userId: subscriber._id,
                  accountStatus: false,
                },
              };
              axios(config)
                .then(function (response) {
                  if (response) {
                    toast.success(
                      "User has been removed as a trustee successfully"
                    );
                    fetchSubscribers();
                  } else {
                    toast.error(
                      response.data.message ||
                        "Failed to remove user as a trustee"
                    );
                  }
                })
                .catch(function (error) {
                  toast.error(
                    error?.response?.data?.message ||
                      "Failed to remove user as a trustee"
                  );
                  errorLogApi(
                    error,
                    "ChurchSubscribersChild",
                    "/user/remove-trustee"
                  );
                });
            }
          },
        },
        {
          label: "No",
        },
      ],
    });
  };

  const handleEnableUser = (subscriber) => {
    confirmAlert({
      title: "Confirm Action",
      message: "Are you sure you want to enable this user?",
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            {
              let userToken = localStorage.getItem("token");
              const config = {
                method: "post",
                url: `${process.env.REACT_APP_API_URL}/user/set-account-status/`,
                headers: {
                  Authorization: `Bearer ${userToken}`,
                },
                data: {
                  userId: subscriber._id,
                  accountStatus: true,
                },
              };
              axios(config)
                .then(function (response) {
                  if (response) {
                    toast.success(
                      "User has been removed as a trustee successfully"
                    );
                    fetchSubscribers();
                  } else {
                    toast.error(
                      response.data.message ||
                        "Failed to remove user as a trustee"
                    );
                  }
                })
                .catch(function (error) {
                  toast.error(
                    error?.response?.data?.message ||
                      "Failed to remove user as a trustee"
                  );
                  errorLogApi(
                    error,
                    "ChurchSubscribersChild",
                    "/user/remove-trustee"
                  );
                });
            }
          },
        },
        {
          label: "No",
        },
      ],
    });
  };

  return (
    <CardBackground>
      <div className="w-full px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="flex flex-col">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Church Subscribers
                </h1>
                <p className="mt-2 text-gray-600">
                  View and manage church subscribers
                </p>
              </div>
              <div className="hidden sm:block">
                <div className="p-3 bg-white shadow-sm rounded-xl">
                  <div className="flex items-center space-x-2">
                    <HiUserGroup className="w-5 h-5 text-indigo-500" />
                    <span className="text-sm font-medium text-gray-600">
                      {filteredData?.length || 0} Subscribers
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Search Bar */}
            <div className="relative mt-6">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <HiSearch className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="Search by name, email or phone..."
                  className="block w-full py-2 pl-10 pr-10 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                {searchTerm && (
                  <button
                    onClick={clearSearch}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                    aria-label="Clear search"
                  >
                    <HiX className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>

            <div className="w-32 h-1 mt-4 bg-indigo-500 rounded-full"></div>
          </div>

          {/* Table */}
          {filteredData && filteredData.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                    >
                      Actions
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort("first_name")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Name</span>
                        {getSortIcon("first_name")}
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort("email")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Email</span>
                        {getSortIcon("email")}
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort("mobile")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Phone</span>
                        {getSortIcon("mobile")}
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort("user_type")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>User Type</span>
                        {getSortIcon("user_type")}
                      </div>
                    </th>
                    {/* <th
                      scope="col"
                      className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort("role")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Role</span>
                        {getSortIcon("role")}
                      </div>
                    </th> */}
                    <th
                      scope="col"
                      className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort("createdAt")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Joined Date</span>
                        {getSortIcon("createdAt")}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentItems.map((subscriber) => (
                    <tr key={subscriber._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <Tooltip title="View Details">
                            <button
                              onClick={() => handleView(subscriber)}
                              className="text-gray-400 hover:text-indigo-500"
                              title="View Details"
                            >
                              <HiEye className="w-5 h-5" />
                            </button>
                          </Tooltip>

                          {subscriber.is_a_trustee !== true && (
                            <Tooltip title="Make Trustee">
                              <button
                                onClick={() => handleMakeTrustee(subscriber)}
                                className="text-gray-400 hover:text-yellow-500"
                                title="Make Trustee"
                              >
                                <HiShieldCheck className="w-5 h-5" />
                              </button>
                            </Tooltip>
                          )}

                          {subscriber.is_a_trustee === true && (
                            <Tooltip title="Remove Trustee">
                              <button
                                onClick={() => handleRemoveTrustee(subscriber)}
                                className="text-gray-400 hover:text-red-500"
                                title="Remove Trustee"
                              >
                                <HiShieldCheck className="w-5 h-5 text-red-500" />
                              </button>
                            </Tooltip>
                          )}

                          {subscriber.secondary_role ? (
                            <Tooltip title="Remove Admin Rights">
                              <button
                                onClick={() => handleRemoveAdmin(subscriber)}
                                className="text-gray-400 hover:text-red-500"
                                title="Remove Admin Rights"
                              >
                                <HiUserCircle className="w-5 h-5 text-red-500" />
                              </button>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Delegate Admin Rights">
                              <button
                                onClick={() => handleDelegateAdmin(subscriber)}
                                className="text-gray-400 hover:text-gray-500"
                                title="Delegate Admin Rights"
                              >
                                <HiUserCircle className="w-5 h-5 text-gray-500" />
                              </button>
                            </Tooltip>
                          )}
                          {/* Enable/Disable User */}
                          {subscriber?.account_status === true && (
                            <Tooltip title="Disable User">
                              <button
                                onClick={() => handleDisableUser(subscriber)}
                                className="text-gray-400 hover:text-red-500"
                                title="Enable/Disable User"
                              >
                                <div
                                  className="p-2 transition-transform duration-200 transform bg-green-500 rounded-full shadow-lg hover:scale-105"
                                  style={{
                                    boxShadow:
                                      "0 4px 6px -1px rgba(0, 220, 0, 0.3), 0 2px 4px -1px rgba(0, 220, 0, 0.2)",
                                  }}
                                ></div>
                              </button>
                            </Tooltip>
                          )}
                          {subscriber?.account_status !== true && (
                            <Tooltip title="Enable User">
                              <button
                                onClick={() => handleEnableUser(subscriber)}
                                className="text-gray-400 hover:text-red-500"
                                title="Enable/Disable User"
                              >
                                <div
                                  className="p-2 transition-transform duration-200 transform bg-red-500 rounded-full shadow-lg hover:scale-105"
                                  style={{
                                    boxShadow:
                                      "0 4px 6px -1px rgba(220, 0, 0, 0.3), 0 2px 4px -1px rgba(220, 0, 0, 0.2)",
                                  }}
                                ></div>
                              </button>
                            </Tooltip>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 w-10 h-10">
                            <div className="flex items-center justify-center w-10 h-10 bg-indigo-100 rounded-full">
                              <span className="text-sm font-medium text-indigo-600">
                                {subscriber.first_name?.[0]}
                                {subscriber.last_name?.[0]}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {subscriber.first_name} {subscriber.last_name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {subscriber.email || "No email"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {subscriber.mobile || "No phone"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getUserTypeBadgeColor(
                            subscriber.user_type
                          )}`}
                        >
                          {subscriber.user_type}
                        </span>
                      </td>
                      {/* <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {subscriber.role}
                        </span>
                      </td> */}
                      <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                        {new Date(subscriber.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
                <div className="flex justify-between flex-1 sm:hidden">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-4 py-2 ml-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing{" "}
                      <span className="font-medium">
                        {indexOfFirstItem + 1}
                      </span>{" "}
                      to{" "}
                      <span className="font-medium">
                        {Math.min(indexOfLastItem, filteredData.length)}
                      </span>{" "}
                      of{" "}
                      <span className="font-medium">{filteredData.length}</span>{" "}
                      results
                    </p>
                  </div>
                  <div>
                    <nav
                      className="inline-flex -space-x-px rounded-md shadow-sm isolate"
                      aria-label="Pagination"
                    >
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-400 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      {[...Array(totalPages)].map((_, index) => (
                        <button
                          key={index + 1}
                          onClick={() => handlePageChange(index + 1)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === index + 1
                              ? "z-10 bg-indigo-50 border-indigo-500 text-indigo-600"
                              : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                          }`}
                        >
                          {index + 1}
                        </button>
                      ))}
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-400 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center p-8 bg-white shadow-sm rounded-xl">
              <HiUserGroup className="w-16 h-16 text-gray-300" />
              <h3 className="mt-4 text-lg font-semibold text-gray-900">
                No Subscribers Found
              </h3>
              <p className="mt-1 text-gray-500">
                {searchTerm
                  ? "No subscribers match your search criteria."
                  : "There are no subscribers to display at the moment."}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* View Subscriber Modal */}
      <ViewSubscriberModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedUser(null);
        }}
        selectedUser={selectedUser}
        getUserTypeBadgeColor={getUserTypeBadgeColor}
      />
    </CardBackground>
  );
};

export default ChurchSubscribersChild;
